/**
 * FlowHub - Centralized Logging Service
 * Production-ready logger with environment-based control
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
}

class Logger {
  private isProduction = import.meta.env.PROD;
  private debugEnabled = import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true';

  /**
   * Debug logs - only in development or when explicitly enabled
   */
  debug(message: string, context?: LogContext): void {
    if (!this.isProduction || this.debugEnabled) {
      console.log(`[DEBUG] ${message}`, context || '');
    }
  }

  /**
   * Info logs - general information
   */
  info(message: string, context?: LogContext): void {
    if (!this.isProduction || this.debugEnabled) {
      console.info(`[INFO] ${message}`, context || '');
    }
  }

  /**
   * Warning logs - potentially problematic situations
   */
  warn(message: string, context?: LogContext): void {
    if (!this.isProduction || this.debugEnabled) {
      console.warn(`[WARN] ${message}`, context || '');
    }
    // In production, send to monitoring service
    if (this.isProduction) {
      this.sendToMonitoring('warn', message, context);
    }
  }

  /**
   * Error logs - critical errors that need attention
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    const errorInfo = error instanceof Error ? {
      message: error.message,
      stack: error.stack,
      name: error.name,
    } : error;

    console.error(`[ERROR] ${message}`, errorInfo, context || '');
    
    // Always send errors to monitoring in production
    if (this.isProduction) {
      this.sendToMonitoring('error', message, { ...context, error: errorInfo });
    }
  }

  /**
   * Send critical logs to external monitoring service
   * Ready for Sentry, LogRocket, or similar integration
   */
  private sendToMonitoring(level: LogLevel, message: string, context?: LogContext): void {
    // TODO: Integrate with Sentry or similar service
    // Example:
    // if (window.Sentry) {
    //   window.Sentry.captureMessage(message, {
    //     level,
    //     extra: context,
    //   });
    // }
    
    // For now, just track in sessionStorage for debugging
    try {
      const logs = JSON.parse(sessionStorage.getItem('app_logs') || '[]');
      logs.push({
        timestamp: new Date().toISOString(),
        level,
        message,
        context,
      });
      // Keep only last 50 logs
      sessionStorage.setItem('app_logs', JSON.stringify(logs.slice(-50)));
    } catch (e) {
      // Silently fail if storage is full
    }
  }

  /**
   * Clear stored logs
   */
  clearLogs(): void {
    sessionStorage.removeItem('app_logs');
  }

  /**
   * Get stored logs for debugging
   */
  getLogs(): any[] {
    try {
      return JSON.parse(sessionStorage.getItem('app_logs') || '[]');
    } catch {
      return [];
    }
  }
}

export const logger = new Logger();
